import type { GridProps } from '@chakra-ui/react'
import { Flex, forwardRef, Grid, Link, Text } from '@chakra-ui/react'
import { SelectionType } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { ReactNode } from 'react'
import React from 'react'

import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { useSelections } from '../../hooks'

export interface RecapSidebarProps extends GridProps {
  logo?: ReactNode
  logoWrapperHeight?: number | undefined
  website?: string | ReactNode
  email?: string | ReactNode
  paymentFrequencyId: number
  paymentPrice: number
  totalPrice: number
  completed: boolean
  sectionItems?: React.ReactNode[]
}

interface SidebarSectionProps extends GridProps {
  isFirst?: boolean
  fullHeight?: boolean
}

interface SidebarItemProps extends GridProps {
  label?: string | ReactNode
  vertical?: boolean
}

export const SectionItem = forwardRef(
  (
    { children, label, vertical = false, sx, ...rest }: SidebarItemProps,
    ref,
  ) => {
    return (
      <Flex
        ref={ref}
        sx={{
          w: '100%',
          py: 2,
          px: 4,
          gap: {
            base: 1,
            lg: 2,
          },
          flexDirection: {
            // base: 'column',
            lg: vertical ? 'column' : 'row',
          },
          flexWrap: 'no-wrap',
          alignItems: { base: 'start', lg: vertical ? 'start' : 'center' },
          justifyContent: 'space-between',
          fontSize: 'sm',
          '&:not(:last-of-type)': {
            borderBottom: '1px solid',
            borderColor: 'white',
          },
          ...sx,
        }}
        {...rest}
      >
        {typeof label === 'string' && <Text>{label}</Text>}

        {typeof label !== 'string' && label}

        {children}
      </Flex>
    )
  },
)

export const SidebarSection = forwardRef(
  (
    {
      children,
      sx,
      isFirst = false,
      fullHeight = false,
      ...rest
    }: SidebarSectionProps,
    ref,
  ) => {
    return (
      <Flex
        ref={ref}
        sx={{
          w: 'auto',
          h: isFirst || fullHeight ? '100%' : 'auto',
          px: {
            base: 0,
            lg: 2,
          },
          borderTopRightRadius: isFirst ? '6px' : 'unset',
          bgColor: 'neutral.50',
          flexDir: 'column',
          justifyContent: 'flex-end',
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Flex>
    )
  },
)

export const RecapSidebar = forwardRef(
  (
    {
      children,
      sx,
      logo,
      logoWrapperHeight,
      website,
      email,
      paymentFrequencyId,
      paymentPrice,
      totalPrice,
      completed,
      sectionItems,
      ...rest
    }: RecapSidebarProps,
    ref,
  ) => {
    const { insuranceParams } = useInsuranceParams()

    const { data: dataPf } = useSelections(SelectionType.FrekvencePlatby)
    const pfEnum = dataPf?.data ?? []

    const logoWrapperMinHeight = logoWrapperHeight
      ? logoWrapperHeight + 72 + 32 - 2
      : 'none'

    return (
      <Grid
        ref={ref}
        sx={{
          h: { lg: '100%' },
          alignContent: 'start',
          gridTemplateRows: { lg: '1fr auto auto' },
          gap: 0.5,
          pr: 0.5,
          py: 0.5,
          ...sx,
        }}
        {...rest}
      >
        {logo && (
          <SidebarSection
            sx={{
              placeContent: 'center',
              placeItems: 'center',
              minHeight: {
                lg: logoWrapperMinHeight,
              },
            }}
            isFirst
          >
            {logo}
          </SidebarSection>
        )}

        <SidebarSection>
          <SectionItem label="www:">
            <Link
              as={'a'}
              href={`${website}`}
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'primary.500', fontWeight: 'medium' }}
            >
              {website}
            </Link>
          </SectionItem>

          {email && (
            <SectionItem label="E-mail:">
              <Link
                as={'a'}
                href={`mailto:${email}`}
                sx={{ color: 'primary.500', fontWeight: 'medium' }}
              >
                {email}
              </Link>
            </SectionItem>
          )}
        </SidebarSection>

        <SidebarSection>
          <SectionItem label="Splátky">
            <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
              {pfEnum.find((x) => x.id === paymentFrequencyId)?.description}
            </Text>
          </SectionItem>

          {paymentPrice > 0 && (
            <SectionItem label="Splátka pojistného">
              <Text sx={{ fontSize: 'md', fontWeight: 'bold' }}>
                {priceFormat(paymentPrice)}
              </Text>
            </SectionItem>
          )}

          {sectionItems &&
            sectionItems.map((x, i) => (
              <React.Fragment key={i}>{x}</React.Fragment>
            ))}
        </SidebarSection>

        {children}
      </Grid>
    )
  },
)
